/* Nuava — fiche produit de la couette (section nuvea-produit).
   Carrousel, bloc d'offre 1 / 2 / 3, lignes couleur + taille, ajout au panier groupé (/cart/add.js, items[]),
   ouverture du tiroir panier d'Horizon, fenêtre du guide des tailles, barre d'achat collante. */

const eventsModule = import('@shopify/events').catch(() => null);
const reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

/* ---------- Prix ---------- */

function formatCompact(cents) {
  const c = Math.max(0, Math.round(Number(cents) || 0));
  /* « 79,99€ » : euro collé, comme l'offre de référence et snippets/nuvea-produit-prix.liquid */
  return (c / 100).toFixed(2).replace('.', ',') + '€';
}

function formatShop(cents, format) {
  const c = Math.max(0, Math.round(Number(cents) || 0));
  const fmt = format || '{{amount_with_comma_separator}}€';
  const group = (value, decimals, thousands, decimal) => {
    const fixed = (value / 100).toFixed(decimals);
    const [whole, frac] = fixed.split('.');
    const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? w + decimal + frac : w;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case 'amount': return group(c, 2, ',', '.');
      case 'amount_no_decimals': return group(c, 0, ',', '.');
      case 'amount_with_comma_separator': return group(c, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(c, 0, '.', ',');
      case 'amount_with_space_separator': return group(c, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator': return group(c, 0, ' ', ',');
      case 'amount_with_apostrophe_separator': return group(c, 2, "'", '.');
      default: return group(c, 2, '.', ',');
    }
  }).replace(/<[^>]*>/g, '');
}

/* Prix d'une option : somme des prix des variantes choisies − remise du palier ; prix barré : somme des prix de vente
   + valeur des taies offertes (même base que le panier et la page de paiement). ok = toutes les variantes existent et sont disponibles. */
function computeOffer(variants, discountCents, giftCents) {
  let sum = 0;
  let compare = Number(giftCents) || 0;
  let ok = variants.length > 0;
  variants.forEach((v) => {
    if (!v) { ok = false; return; }
    sum += v.price;
    compare += v.price;
    if (!v.available) ok = false;
  });
  const total = Math.max(0, sum - (Number(discountCents) || 0));
  return { sum, total, compare, ok, showCompare: compare > total };
}

/* Regroupe les couettes identiques : deux unités de la même variante = une ligne, quantité 2 */
function groupItems(variants) {
  const map = new Map();
  variants.forEach((v) => {
    if (!v) return;
    map.set(v.id, (map.get(v.id) || 0) + 1);
  });
  return [...map].map(([id, quantity]) => ({ id, quantity }));
}

/* Produits de taies pour n couettes d'une même couleur : « 6 taies » par groupe de 3 couettes, puis « 2 » ou « 4 » pour le reste */
function taiesLots(n) {
  const lots = [];
  for (let k = Math.floor(n / 3); k > 0; k--) lots.push('6');
  if (n % 3 === 1) lots.push('2');
  if (n % 3 === 2) lots.push('4');
  return lots;
}

/* Taies offertes avec les couettes choisies, dans leur couleur (données #nuvea-taies-data du layout) */
function taiesFor(variants) {
  let map = null;
  try { map = JSON.parse(document.getElementById('nuvea-taies-data').textContent).variantes; } catch (e) { return []; }
  const perColor = new Map();
  variants.forEach((v) => {
    if (!v) return;
    const color = (v.options || []).find((o) => map['2'][o]);
    if (color) perColor.set(color, (perColor.get(color) || 0) + 1);
  });
  const counts = new Map();
  perColor.forEach((n, color) => taiesLots(n).forEach((size) => {
    const id = map[size][color];
    counts.set(id, (counts.get(id) || 0) + 1);
  }));
  return [...counts].map(([id, quantity]) => ({ id, quantity }));
}

window.NuveaProduit = { computeOffer, groupItems, taiesFor, formatCompact, formatShop, instances: [] };

/* ---------- Carrousel (repris de la fiche maison : scroll-snap, flèches, points, vignettes, clavier) ---------- */

function initCarousel(root) {
  const car = root.querySelector('[data-nuvea-car]');
  if (!car) return { goToMedia() {} };
  const track = car.querySelector('[data-car-track]');
  const slides = track.querySelectorAll('.nuvea-car__diapo');
  const dots = car.querySelectorAll('.nuvea-car__point');
  const thumbs = car.querySelectorAll('[data-car-go]');
  const prev = car.querySelector('[data-car-prev]');
  const next = car.querySelector('[data-car-next]');
  const cur = car.querySelector('[data-car-cur]');
  let index = 0;
  let ticking = false;
  let target = -1;
  let settle;

  const paint = (i) => {
    index = i;
    dots.forEach((d, k) => d.classList.toggle('is-on', k === i));
    thumbs.forEach((t, k) => {
      t.classList.toggle('is-on', k === i);
      if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
    });
    if (cur) cur.textContent = String(i + 1);
    if (prev) prev.disabled = i === 0;
    if (next) next.disabled = i === slides.length - 1;
  };
  const current = () => Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / (track.clientWidth || 1))));
  const release = () => { target = -1; clearTimeout(settle); };
  const go = (i, instant) => {
    i = Math.max(0, Math.min(slides.length - 1, i));
    target = i;
    clearTimeout(settle);
    settle = setTimeout(() => { release(); paint(current()); }, 1000);
    track.scrollTo({ left: i * track.clientWidth, behavior: instant ? 'instant' : (reduceMotion ? 'auto' : 'smooth') });
    paint(i);
  };
  track.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      if (target !== -1) {
        if (current() !== target) return;
        release();
      }
      paint(current());
    });
  }, { passive: true });
  track.addEventListener('touchstart', release, { passive: true });
  if (prev) prev.addEventListener('click', () => go(index - 1));
  if (next) next.addEventListener('click', () => go(index + 1));
  thumbs.forEach((t) => t.addEventListener('click', () => go(parseInt(t.dataset.carGo, 10))));
  track.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
    else if (e.key === 'Home') { e.preventDefault(); go(0); }
    else if (e.key === 'End') { e.preventDefault(); go(slides.length - 1); }
  });
  let lastW = -1;
  const realign = () => {
    const w = track.clientWidth;
    if (lastW === -1) { lastW = w; return; }
    if (w && w !== lastW) { lastW = w; go(index, true); }
  };
  if ('ResizeObserver' in window) new ResizeObserver(realign).observe(track);
  else { lastW = track.clientWidth; window.addEventListener('resize', realign); }

  return {
    goToMedia(id, instant) {
      if (!id) return;
      for (let k = 0; k < slides.length; k++) {
        if (slides[k].dataset.mediaId === String(id)) { go(k, instant); return; }
      }
    },
  };
}

/* ---------- Fenêtre du guide des tailles ---------- */

function initDialog(root) {
  const dialog = root.querySelector('[data-nuvea-dialog]');
  if (!dialog) return;
  let opener = null;
  const HASH = '#guide-des-tailles';
  const open = (from) => {
    opener = from || document.activeElement;
    if (dialog.open) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('nuvea-dialog-ouvert');
  };
  const unlock = () => document.documentElement.classList.remove('nuvea-dialog-ouvert');
  const close = () => {
    unlock();
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
  };
  dialog.addEventListener('cancel', unlock);
  dialog.addEventListener('close', () => {
    unlock();
    if (window.location.hash === HASH && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (opener && typeof opener.focus === 'function') opener.focus();
    opener = null;
  });
  /* Échap : géré nativement par <dialog> ; repli pour les navigateurs sans showModal */
  dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape' && typeof dialog.showModal !== 'function') close(); });
  /* Clic sur le fond : ferme */
  dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
  dialog.querySelectorAll('[data-nuvea-dialog-close]').forEach((b) => b.addEventListener('click', close));
  root.querySelectorAll('[data-nuvea-guide-open]').forEach((a) => {
    a.addEventListener('click', (e) => { e.preventDefault(); open(a); });
  });
  const fromHash = () => { if (window.location.hash === HASH && dialog.isConnected) open(null); };
  window.addEventListener('hashchange', fromHash);
  fromHash();
  /* Retire l'écouteur global quand la section est déchargée (éditeur de thème) */
  const destroy = () => window.removeEventListener('hashchange', fromHash);
  return { open, close, destroy };
}

/* ---------- Menu des couleurs avec pastilles ----------
   Le <select> natif reste la source de vérité (prix, galerie, panier) : il est seulement masqué,
   et la liste maison le pilote en déclenchant son événement « change ». */

function enhanceColorSelect(sel, colors) {
  const wrap = sel.closest('.nuvea-select');
  if (!wrap || wrap.dataset.nuveaCs) return null;
  wrap.dataset.nuveaCs = '1';
  wrap.classList.add('nuvea-select--perso');
  const label = wrap.querySelector('label');
  const listId = sel.id + '-liste';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'nuvea-select__champ nuvea-cs__bouton';
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-controls', listId);
  const txt = document.createElement('span');
  txt.className = 'nuvea-cs__texte';
  btn.appendChild(txt);

  const list = document.createElement('ul');
  list.className = 'nuvea-cs__liste';
  list.id = listId;
  list.setAttribute('role', 'listbox');
  if (label) list.setAttribute('aria-label', label.textContent.trim());
  list.hidden = true;
  const items = [...sel.options].map((o) => {
    const li = document.createElement('li');
    li.className = 'nuvea-cs__option';
    li.setAttribute('role', 'option');
    li.tabIndex = -1;
    li.dataset.value = o.value;
    const dot = document.createElement('span');
    dot.className = 'nuvea-cs__pastille';
    dot.setAttribute('aria-hidden', 'true');
    if (colors[o.value]) dot.style.setProperty('--nuvea-pastille', colors[o.value]);
    li.append(dot, document.createTextNode(o.textContent));
    list.appendChild(li);
    return li;
  });

  sel.classList.add('nuvea-cs__natif');
  sel.tabIndex = -1;
  sel.setAttribute('aria-hidden', 'true');
  if (label) {
    label.removeAttribute('for');
    btn.setAttribute('aria-label', label.textContent.trim());
  }
  sel.after(btn, list);

  const sync = () => {
    txt.textContent = sel.value;
    items.forEach((li) => li.setAttribute('aria-selected', String(li.dataset.value === sel.value)));
  };
  const isOpen = () => !list.hidden;
  const close = (focusBtn) => {
    if (!isOpen()) return;
    list.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    if (focusBtn) btn.focus();
  };
  const open = () => {
    if (isOpen()) return;
    sync();
    list.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    const cur = items.find((li) => li.dataset.value === sel.value) || items[0];
    if (cur) {
      cur.focus({ preventScroll: true });
      cur.scrollIntoView({ block: 'nearest' });
    }
  };
  const choose = (li) => {
    if (li && sel.value !== li.dataset.value) {
      sel.value = li.dataset.value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    sync();
    close(true);
  };

  btn.addEventListener('click', () => (isOpen() ? close(false) : open()));
  btn.addEventListener('keydown', (e) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      open();
    }
  });
  list.addEventListener('click', (e) => {
    const li = e.target.closest('.nuvea-cs__option');
    if (li) choose(li);
  });
  list.addEventListener('keydown', (e) => {
    const i = items.indexOf(document.activeElement);
    const go = (k) => { const li = items[Math.max(0, Math.min(items.length - 1, k))]; if (li) li.focus(); };
    if (e.key === 'ArrowDown') { e.preventDefault(); go(i + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); go(i - 1); }
    else if (e.key === 'Home') { e.preventDefault(); go(0); }
    else if (e.key === 'End') { e.preventDefault(); go(items.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(items[i]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'Tab') close(false);
  });
  document.addEventListener('pointerdown', (e) => {
    if (isOpen() && !wrap.contains(e.target)) close(false);
  });
  sync();
  return { sync };
}

/* ---------- Bloc d'offre et ajout au panier ---------- */

function initOffer(root, data, carousel) {
  const fmt = (c) => (data.style === 'shop' ? formatShop(c, data.moneyFormat) : formatCompact(c));
  const form = root.querySelector('.nuvea-form');
  if (!form) return null;
  const cards = [...root.querySelectorAll('[data-nuvea-carte]')];
  const radios = cards.map((c) => c.querySelector('input[type="radio"]'));
  const panel = root.querySelector('[data-nuvea-lignes]');
  const rows = panel ? [...panel.querySelectorAll('[data-nuvea-ligne]')] : [];
  const hint = panel ? panel.querySelector('[data-nuvea-aide]') : null;
  const idInput = form.querySelector('[data-nuvea-id]');
  const cta = form.querySelector('[data-nuvea-cta]');
  const status = form.querySelector('[data-nuvea-statut]');
  const stickyPrice = root.querySelector('[data-nuvea-collant-prix]');
  const stickyBarre = root.querySelector('[data-nuvea-collant-barre]');
  const byId = new Map(data.variants.map((v) => [String(v.id), v]));
  const taiesImgs = (() => {
    const el = root.querySelector('[data-nuvea-taies-imgs]');
    try { return el ? JSON.parse(el.dataset.nuveaTaiesImgs) : null; } catch (e) { return null; }
  })();
  const checked = radios.findIndex((r) => r && r.checked);
  let tier = checked >= 0 ? checked + 1 : (data.defaultTier || 1);

  const colorMenus = data.colorIndex >= 0
    ? rows.map((row) => row.querySelector('select[data-option-index="' + data.colorIndex + '"]'))
        .filter(Boolean).map((sel) => enhanceColorSelect(sel, data.colors)).filter(Boolean)
    : [];

  const optionCount = data.variants.length ? data.variants[0].options.length : 0;
  const findVariant = (opts) => data.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
  const rowVariant = (row) => {
    const selects = [...row.querySelectorAll('select[data-option-index]')];
    if (!selects.length) return byId.get(String(data.defaultVariant)) || data.variants[0] || null;
    const opts = [];
    for (let i = 0; i < optionCount; i++) {
      const s = selects.find((x) => Number(x.dataset.optionIndex) === i);
      opts.push(s ? s.value : null);
    }
    return findVariant(opts);
  };

  const setStatus = (msg) => {
    if (!status) return;
    status.textContent = msg || '';
    status.hidden = !msg;
  };

  const refresh = () => {
    const variants = rows.map(rowVariant);
    rows.forEach((row, i) => {
      row.hidden = i >= tier;
      const v = variants[i];
      row.classList.toggle('is-indispo', !v || !v.available);
      const img = row.querySelector('[data-nuvea-vignette]');
      const src = (v && v.img) || data.fallbackImg;
      if (img && src && img.getAttribute('src') !== src) img.setAttribute('src', src);
      const pastille = row.querySelector('[data-nuvea-pastille]');
      if (pastille && data.colorIndex >= 0) {
        const sel = row.querySelector('select[data-option-index="' + data.colorIndex + '"]');
        if (sel && data.colors[sel.value]) pastille.style.setProperty('--nuvea-pastille', data.colors[sel.value]);
      }
    });
    /* La phrase « les autres couettes reprennent… » n'a de sens qu'à partir de 2 couettes */
    if (hint) hint.hidden = tier < 2;
    /* Les lignes recopiées par le script changent le <select> sans événement : on resynchronise les menus à pastilles */
    colorMenus.forEach((m) => m.sync());
    cards.forEach((card, i) => {
      const n = i + 1;
      const res = computeOffer(variants.slice(0, n), data.discounts[i], (data.gifts || [])[i]);
      const priceEl = card.querySelector('[data-nuvea-prix]');
      const barre = card.querySelector('[data-nuvea-barre]');
      const barreVal = card.querySelector('[data-nuvea-barre-val]');
      if (priceEl) priceEl.textContent = fmt(res.total);
      if (barreVal) barreVal.textContent = fmt(res.compare);
      if (barre) barre.hidden = !res.showCompare;
      card.classList.toggle('is-selected', n === tier);
      /* Vignette de la carte et image des taies offertes : couleur de la 1re couette */
      const v0 = variants[0];
      const thumb = card.querySelector('[data-nuvea-carte-vignette]');
      const thumbSrc = (v0 && v0.img) || data.fallbackImg;
      if (thumb && thumbSrc && thumb.getAttribute('src') !== thumbSrc) thumb.setAttribute('src', thumbSrc);
      const taiesImg = card.querySelector('[data-nuvea-taies-img]');
      const couleur = v0 && data.colorIndex >= 0 ? v0.options[data.colorIndex] : null;
      const taiesSrc = taiesImgs && couleur ? taiesImgs[couleur] : null;
      if (taiesImg && taiesSrc && taiesImg.getAttribute('src') !== taiesSrc) taiesImg.setAttribute('src', taiesSrc);
    });
    const res = computeOffer(variants.slice(0, tier), data.discounts[tier - 1], (data.gifts || [])[tier - 1]);
    if (stickyPrice) stickyPrice.textContent = fmt(res.total);
    if (stickyBarre) {
      stickyBarre.textContent = fmt(res.compare);
      stickyBarre.hidden = !res.showCompare;
    }
    if (cta) cta.disabled = !res.ok;
    setStatus(res.ok ? '' : data.msg.indispo);
    if (idInput && variants[0]) idInput.value = variants[0].id;
    return { variants: variants.slice(0, tier), offer: res };
  };

  const placePanel = () => {
    const card = cards[tier - 1];
    const slot = card && card.querySelector('[data-nuvea-slot]');
    if (panel && slot && panel.parentElement !== slot) slot.appendChild(panel);
  };

  /* Choix que le client a déjà réglés lui-même, option par option (clé « ligne:option ») : on ne les écrase jamais.
     Changer la couleur de la couette 2 ne la détache donc pas de la taille de la 1re, et inversement. */
  const touched = new Set();
  const touchKey = (k, optionIndex) => k + ':' + optionIndex;

  /* Recopie dans la ligne k les options de la ligne 1 que le client n'y a pas réglées lui-même */
  const copyFirstRow = (k) => {
    const row = rows[k];
    if (!rows[0] || !row || k === 0) return;
    rows[0].querySelectorAll('select[data-option-index]').forEach((src) => {
      if (touched.has(touchKey(k, src.dataset.optionIndex))) return;
      const dst = row.querySelector('select[data-option-index="' + src.dataset.optionIndex + '"]');
      if (dst && dst.value !== src.value) dst.value = src.value;
    });
  };

  /* Adresse : ?variant= suit la variante de la ligne 1, sans recharger ni ajouter d'entrée à l'historique */
  const syncUrl = (v) => {
    if (!v || !window.history || typeof window.history.replaceState !== 'function') return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') === String(v.id)) return;
      url.searchParams.set('variant', String(v.id));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) { /* adresse non modifiable : sans conséquence */ }
  };

  radios.forEach((r, i) => {
    if (!r) return;
    r.addEventListener('change', () => {
      if (!r.checked) return;
      const previous = tier;
      tier = i + 1;
      /* Les couettes qui apparaissent reprennent la couleur et la taille de la 1re */
      for (let k = Math.max(1, previous); k < tier; k++) copyFirstRow(k);
      placePanel();
      refresh();
    });
  });

  rows.forEach((row, i) => {
    row.querySelectorAll('select').forEach((sel) => {
      sel.addEventListener('change', () => {
        touched.add(touchKey(i, sel.dataset.optionIndex));
        if (i === 0) {
          /* Les autres lignes suivent la 1re pour chaque option jamais réglée à la main, visibles ou non */
          for (let k = 1; k < rows.length; k++) copyFirstRow(k);
        }
        const state = refresh();
        if (i === 0) {
          const v = state.variants[0] || rowVariant(row);
          /* Seule la couleur de la 1re couette pilote la photo de la galerie : changer la taille ne la déplace pas */
          const isColor = data.colorIndex < 0 || Number(sel.dataset.optionIndex) === data.colorIndex;
          if (isColor && v && v.media) carousel.goToMedia(v.media);
          syncUrl(v);
        }
      });
    });
  });

  /* Variante imposée par l'URL (?variant=) : la galerie s'y place sans animation */
  if (data.selectedVariant) {
    const v = byId.get(String(data.selectedVariant));
    if (v && v.media) carousel.goToMedia(v.media, true);
  }

  placePanel();
  refresh();

  /* Ajout au panier : toutes les couettes en un seul appel, puis tiroir panier */
  /* Theme est une const globale d'Horizon (snippets/scripts.liquid) : elle n'est pas une propriété de window */
  const routes = (typeof Theme !== 'undefined' && Theme && Theme.routes) || {};
  const cartUrl = routes.cart_url || '/cart';
  const addUrl = routes.cart_add_url || '/cart/add.js';

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!cta || cta.getAttribute('aria-busy') === 'true') return;
    const state = refresh();
    if (!state.offer.ok) { setStatus(data.msg.indispo); return; }
    const couettes = groupItems(state.variants);
    if (!couettes.length) { setStatus(data.msg.indispo); return; }
    /* Taies d'abord : le panier affiche le dernier article ajouté en tête, la couette passe donc au-dessus */
    const items = taiesFor(state.variants).concat(couettes);

    cta.setAttribute('aria-busy', 'true');
    cta.disabled = true;
    setStatus('');

    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId))];
    const body = { items };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const events = await eventsModule;
    const Ev = events && events.CartLinesUpdateEvent;
    let deferred = null;
    if (Ev) {
      deferred = Ev.createPromise();
      deferred.promise.catch(() => {});
      form.dispatchEvent(new Ev({
        action: 'add',
        context: 'product',
        lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
        promise: deferred.promise,
      }));
    }
    const fetchCart = () => fetch(cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      const cart = await fetchCart().catch(() => null);

      if (!response.ok || result.status) {
        if (deferred) {
          if (cart) deferred.resolve({ cart: Ev.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'nuvea-produit' } });
          else deferred.reject(new Error('cart'));
        }
        const detail = result.description || result.message;
        setStatus(detail ? data.msg.erreur + ' (' + detail + ')' : data.msg.erreur);
        return;
      }

      if (deferred) {
        if (cart) {
          deferred.resolve({
            cart: Ev.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, itemCount: items.reduce((s, it) => s + it.quantity, 0), source: 'nuvea-produit', sections: result.sections, didError: false },
          });
        } else {
          deferred.reject(new Error('cart'));
        }
      }
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function' && Ev) {
        if (!drawer.isOpen) drawer.open();
      } else {
        window.location.href = cartUrl;
      }
    } catch (error) {
      if (deferred) deferred.reject(error);
      setStatus(data.msg.erreur);
    } finally {
      cta.removeAttribute('aria-busy');
      refresh();
    }
  });

  return { refresh, rowVariant, get tier() { return tier; }, form, cta };
}

/* ---------- Barre d'achat collante (mobile) ---------- */

function initSticky(root, offer) {
  const bar = root.querySelector('[data-nuvea-collant]');
  if (!bar || !offer || !offer.cta || !('IntersectionObserver' in window)) return;
  const btn = bar.querySelector('[data-nuvea-collant-btn]');
  let pastCta = false;
  let footerIn = false;
  const update = () => {
    const show = pastCta && !footerIn;
    bar.classList.toggle('is-visible', show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (show) bar.removeAttribute('inert'); else bar.setAttribute('inert', '');
    document.documentElement.classList.toggle('nuvea-collant-on', pastCta);
  };
  new IntersectionObserver((entries) => {
    const e = entries[0];
    pastCta = !e.isIntersecting && e.boundingClientRect.top < 0;
    update();
  }).observe(offer.cta);
  const footer = document.querySelector('footer, .shopify-section-group-footer-group');
  if (footer) new IntersectionObserver((entries) => { footerIn = entries[0].isIntersecting; update(); }).observe(footer);
  if (btn) {
    btn.addEventListener('click', () => {
      if (offer.cta.disabled) {
        root.querySelector('.nuvea-offre')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      if (offer.form.requestSubmit) offer.form.requestSubmit(offer.cta);
      else offer.form.dispatchEvent(new Event('submit', { cancelable: true }));
    });
  }
}

/* ---------- Démarrage ---------- */

function init(root) {
  if (root.dataset.nuveaInit) return;
  root.dataset.nuveaInit = '1';
  let data;
  try {
    data = JSON.parse(root.querySelector('[data-nuvea-data]').textContent);
  } catch (e) {
    console.warn('Nuava : données de la fiche illisibles', e);
    return;
  }
  data.msg = data.msg || {};
  data.colors = data.colors || {};
  data.discounts = data.discounts || [0, 0, 0];
  root.classList.add('nuvea-js');
  const carousel = initCarousel(root);
  const offer = initOffer(root, data, carousel);
  const dialog = initDialog(root);
  initSticky(root, offer);
  const instance = { root, data, offer, dialog };
  window.NuveaProduit.instances.push(instance);

  /* Éditeur de thème : section déchargée → on retire l'écouteur hashchange et l'instance */
  const onUnload = (e) => {
    if (!(e.target instanceof Node) || !e.target.contains(root)) return;
    if (dialog && dialog.destroy) dialog.destroy();
    const list = window.NuveaProduit.instances;
    const k = list.indexOf(instance);
    if (k !== -1) list.splice(k, 1);
    document.removeEventListener('shopify:section:unload', onUnload);
  };
  document.addEventListener('shopify:section:unload', onUnload);
}

const start = () => document.querySelectorAll('[data-nuvea-produit]').forEach(init);
start();
document.addEventListener('shopify:section:load', start);
